import api from './api';
import type {
  UpdateColaboradorContatoContract,
  UpdateColaboradorContatoResponseContract,
  AdmissaoRetroativaPayloadContract,
  DesligamentoSemDemissionalPayloadContract,
  TransferirColaboradoresLoteContract,
  TransferirColaboradoresLoteResponseContract,
  FiliaisGrupoEconomicoResponseContract,
} from '@sstexames/shared-types';

export interface Colaborador {
  id: number;
  nome: string;
  nome_social?: string | null;
  nome_mae?: string | null;
  pcd?: boolean;
  enviar_esocial?: boolean;
  regime_contratacao?: string | null;
  estado_civil?: string | null;
  cpf: string;
  rg?: string | null;
  email: string;
  telefone: string | null;
  data_nascimento: string;
  data_admissao: string | null;
  data_demissao: string | null;
  sexo: string | null;
  is_candidato?: boolean;
  candidato_status?: 'pendente_aprovacao' | 'aprovado' | 'reprovado' | 'desistencia' | null;
  candidato_aprovado_em?: string | null;
  origem?: string | null;
  tipo_id?: number | null;
  ultimo_periodico?: string | null;
  tempo_periodico?: number | null;
  formacao_academica_id?: number | null;
  cargo: {
    id: number;
    nome: string;
  } | null;
  setor: {
    id: number;
    nome: string;
  } | null;
  cargo_id?: number | null;
  setor_id?: number | null;
  cargo_nome?: string | null;
  setor_nome?: string | null;
  empresa_id: number;
  enabled: boolean | null;
  matricula_esocial?: string | null;
  transferido_em?: string | null;
  transferido_de_empresa_id?: number | null;
  recem_transferido?: boolean;
  empresa_transferencia_origem?: {
    id: number;
    nome: string;
    razao_social: string;
    cnpj?: string | null;
  } | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ListColaboradoresParams {
  page?: number;
  limit?: number;
  search?: string;
  enabled?: boolean | 'all';
  empresa_id?: number;
}

export interface CreateColaboradorDto {
  id?: number;
  nome: string;
  cpf: string;
  rg?: string;
  email?: string;
  telefone?: string;
  data_nascimento?: string;
  data_admissao?: string;
  data_demissao?: string;
  sexo?: string;
  formacao_academica_id?: number;
  cargo_id?: number;
  setor_id?: number;
  tipo_id?: number;
  cargo_nome?: string; // Para permitir inserir livre
  setor_nome?: string; // Para permitir inserir livre
  empresa_id?: number; // Opcional, a API pega do token
  ultimo_periodico?: string;
  tempo_periodico?: number;
  matricula_esocial?: string;
  nome_social?: string;
  nome_mae?: string;
  pcd?: boolean;
  enviar_esocial?: boolean;
  regime_contratacao?: string;
  tipo_contrato?: string;
  estado_civil?: string;
  is_candidato?: boolean;
  candidato_status?: 'pendente_aprovacao' | 'aprovado' | 'reprovado';
}

export interface ColaboradorEndereco {
  id?: number;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string | null;
  bairro?: string;
  municipio?: string;
  cidade?: string;
  uf?: string;
  estado?: string;
}

export interface ColaboradorFicha {
  id: number;
  nome: string;
  nome_social?: string | null;
  nome_mae?: string | null;
  pcd?: boolean;
  enviar_esocial?: boolean;
  regime_contratacao?: string | null;
  estado_civil?: string | null;
  cpf: string;
  rg?: string | null;
  email: string;
  telefone: string | null;
  data_nascimento: string | null;
  data_admissao: string | null;
  data_demissao: string | null;
  sexo: string | null;
  matricula_esocial?: string | null;
  formacao_academica_id?: number | null;
  formacao_academica_nome?: string | null;
  cargo: { id: number; nome: string } | null;
  setor: { id: number; nome: string } | null;
  endereco: ColaboradorEndereco | null;
  aso?: ColaboradorAsoResumoContract | null;
}

export interface UpdateColaboradorDto {
  nome?: string;
  cpf?: string;
  rg?: string;
  email?: string;
  telefone?: string;
  data_nascimento?: string;
  data_admissao?: string;
  data_demissao?: string;
  sexo?: string;
  formacao_academica_id?: number | null;
  cargo_id?: number;
  setor_id?: number;
  cargo_nome?: string;
  setor_nome?: string;
  ultimo_periodico?: string;
  tempo_periodico?: number;
  enabled?: boolean;
  matricula_esocial?: string;
  nome_social?: string;
  nome_mae?: string | null;
  pcd?: boolean;
  enviar_esocial?: boolean;
  regime_contratacao?: string;
  estado_civil?: string;
  is_candidato?: boolean;
  candidato_status?: 'pendente_aprovacao' | 'aprovado' | 'reprovado';
  endereco?: ColaboradorEndereco;
}

export interface AgendamentoColaborador {
  id: number;
  data_agendamento: string;
  hora_agendamento: string | null;
  exame: string;
  clinica: {
    id: number;
    nome: string;
    endereco: string | null;
  };
  status: string;
  tipo: string;
  avulso?: boolean;
  pedido_id?: number;
  exame_clinico_nome?: string;
  pedido?: {
    id: number;
    status: string;
    tipo_pagamento?: string;
  };
}

export interface HistoricoColaborador {
  id: number;
  tipo: 'exame' | 'cadastro' | 'alteracao';
  acao: string;
  data: string;
  usuario: string;
  detalhes: string;
}

import type {
  CandidatoPendenteContract,
  ColaboradorAsoResumoContract,
  DecisaoCandidatoContract,
} from '@sstexames/shared-types';

export const colaboradoresService = {
  async findAll(params?: ListColaboradoresParams): Promise<PaginatedResponse<Colaborador>> {
    const response = await api.get<PaginatedResponse<Colaborador>>('/colaboradores', {
      params: {
        enabled: true,
        ...params,
      },
    });
    return response.data;
  },

  async findOne(id: number): Promise<Colaborador> {
    const response = await api.get<Colaborador>(`/colaboradores/${id}`);
    return response.data;
  },

  async getFicha(id: number): Promise<ColaboradorFicha> {
    const response = await api.get<ColaboradorFicha>(`/colaboradores/${id}/ficha`);
    return response.data;
  },

  async create(data: CreateColaboradorDto): Promise<Colaborador> {
    const response = await api.post<Colaborador>('/colaboradores', data);
    return response.data;
  },

  async update(id: number, data: UpdateColaboradorDto): Promise<Colaborador> {
    const response = await api.patch<Colaborador>(`/colaboradores/${id}`, data);
    return response.data;
  },

  async remove(id: number): Promise<void> {
    await api.delete(`/colaboradores/${id}`);
  },

  async getAgendamentos(id: number): Promise<AgendamentoColaborador[]> {
    const response = await api.get<AgendamentoColaborador[]>(`/colaboradores/${id}/agendamentos`);
    return response.data;
  },

  async getHistorico(id: number): Promise<HistoricoColaborador[]> {
    const response = await api.get<HistoricoColaborador[]>(`/colaboradores/${id}/historico`);
    return response.data;
  },

  async countCandidatosPendentes(): Promise<number> {
    const response = await api.get<{ count: number }>('/colaboradores/candidatos/count');
    return response.data?.count || 0;
  },

  async findCandidatos(status?: string): Promise<CandidatoPendenteContract[]> {
    const response = await api.get<CandidatoPendenteContract[]>('/colaboradores/candidatos', {
      params: status ? { status } : undefined,
    });
    return response.data;
  },

  async aprovarCandidato(
    id: number,
    data?: DecisaoCandidatoContract,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.patch<{ success: boolean; message: string }>(
      `/colaboradores/candidatos/${id}/aprovar`,
      data || {},
    );
    return response.data;
  },

  async reprovarCandidato(
    id: number,
    data?: { motivo?: string; tipo?: 'reprovado' | 'desistencia' },
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.patch<{ success: boolean; message: string }>(
      `/colaboradores/candidatos/${id}/reprovar`,
      data || {},
    );
    return response.data;
  },

  async descartarCandidato(
    id: number,
    data?: { motivo?: string },
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.patch<{ success: boolean; message: string }>(
      `/colaboradores/candidatos/${id}/descartar`,
      { tipo: 'desistencia', ...data },
    );
    return response.data;
  },

  async downloadCandidatoAso(id: number): Promise<Blob> {
    const response = await api.get(`/colaboradores/candidatos/${id}/aso/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async downloadColaboradorAso(id: number): Promise<Blob> {
    const response = await api.get(`/colaboradores/${id}/aso/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async atualizarContato(
    id: number | string,
    data: UpdateColaboradorContatoContract,
  ): Promise<UpdateColaboradorContatoResponseContract> {
    const response = await api.patch<UpdateColaboradorContatoResponseContract>(
      `/colaboradores/${id}/contato`,
      data,
    );
    return response.data;
  },

  async getColaboradores(params?: ListColaboradoresParams): Promise<PaginatedResponse<Colaborador>> {
    return this.findAll(params);
  },

  async admissaoRetroativa(
    data: AdmissaoRetroativaPayloadContract,
  ): Promise<{ success: boolean; message: string; display_auditoria: string; data: Colaborador }> {
    const response = await api.post<{ success: boolean; message: string; display_auditoria: string; data: Colaborador }>(
      '/colaboradores/admissao-retroativa',
      data,
    );
    return response.data;
  },

  async desligamentoSemDemissional(
    data: DesligamentoSemDemissionalPayloadContract,
  ): Promise<{ success: boolean; message: string; data: Colaborador }> {
    const response = await api.post<{ success: boolean; message: string; data: Colaborador }>(
      '/colaboradores/desligamento-sem-demissional',
      data,
    );
    return response.data;
  },

  async transferirEmLote(
    data: TransferirColaboradoresLoteContract,
  ): Promise<TransferirColaboradoresLoteResponseContract> {
    const response = await api.post<TransferirColaboradoresLoteResponseContract>(
      '/colaboradores/transferir-lote',
      data,
    );
    return response.data;
  },

  async getFiliais(): Promise<FiliaisGrupoEconomicoResponseContract> {
    const response = await api.get<FiliaisGrupoEconomicoResponseContract>(
      '/colaboradores/filiais',
    );
    return response.data;
  },
};



